// Chemin: C:\gestion-copro\src\pages\admin\DocumentsPage.tsx
// Session 66 : Liaison Affaire ↔ GED automatique (Phase 5 GED)
// Session 65 : Intégration SENTINELLE Gardien + Archiviste (flux complet)
// Session 64 : Refonte GED Phase 2 - Vues multiples (Catégories / Affaires / Liste)
// Session 75 : Sécurité suppression (corbeille visible uniquement pour Gestionnaire/Admin SaaS)
// Session 83 : UX Recherche - Affichage direct des résultats sans accordéons
// Session 106 : Utilisation catégorie directe SENTINELLE (taxonomie GED v2)
// Session 120 : Persistance anomalies + donneesCles + analyseSentinelle dans Firestore (alignement VIGIE)

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, doc, setDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { TypeDocument, TypeDocumentGED, CategorieDocument } from '../../types/document.types';
import { useCopropriete } from '../../context/CoproprieteContext';
import { 
  deleteDocument, 
  getTypeDocumentLabel, 
  getTypeColor,
  getCategorieLabel,
  TYPES_DOCUMENT
} from '../../services/document.service';
import { CATEGORIES_DOCUMENT_OPTIONS } from '../../constants/documentTaxonomy';
import DocumentCategoryAccordion from '../../components/documents/DocumentCategoryAccordion';
import DocumentAffaireGroup from '../../components/documents/DocumentAffaireGroup';

// Import SENTINELLE Gardien
import { analyserAvantUpload, AnalyseGardienResponse, verifierFichier } from '../../services/gardien.service';
import SentinelleGardienModal from '../../components/sentinelle/SentinelleGardienModal';

// Import SENTINELLE Archiviste
import { 
  analyzeDocument as analyserArchiviste, 
  uploadTempFile, 
  deleteTempFile,
  DocumentSuggestion 
} from '../../services/documentAnalysis.service';

// Import service Affaire pour liaison automatique (Session 66)
import { getAffairesByCopropriete, rattacherDocument as rattacherDocumentAffaire } from '../../services/affaire.service';
import { Affaire } from '../../types/affaire.types';

// Types de vue disponibles
type ViewMode = 'categories' | 'affaires' | 'liste';

// Interface locale pour les documents chargés
interface DocumentData {
  id: string;
  coproprieteId: string;
  titre: string;
  description?: string;
  type: TypeDocument;
  categorie?: CategorieDocument;
  fichierUrl: string;
  fichierNom: string;
  fichierTaille: number;
  fichierType: string;
  interlocuteurId?: string;
  interlocuteurNom?: string;
  dateDocument: any;
  uploadeParNom: string;
  createdAt: any;
  visibilite?: 'cs_only' | 'public';
  affaireId?: string;
  affaireTitre?: string;
}

interface Interlocuteur {
  id: string;
  raisonSociale: string;
  type: string;
}

// Session 106 : SENTINELLE retourne maintenant directement typeDocument et categorie
// en taxonomie GED v2. Le mapping n'est plus nécessaire pour les nouvelles analyses.
// On garde le mapping comme fallback pour d'éventuels anciens types résiduels.
const mapTypeArchivisteVersGED = (typeArchiviste: string): TypeDocumentGED => {
  // Si c'est déjà un type GED v2 valide, le retourner directement
  const typesGEDv2: TypeDocumentGED[] = [
    'pv', 'contrat', 'facture', 'devis', 'courrier', 'email', 'rapport', 'plan',
    'attestation', 'reglement', 'edd', 'dpe', 'permis', 'photo', 'note',
    'annexe', 'appel_fonds', 'budget', 'releve', 'autre'
  ];
  if (typesGEDv2.includes(typeArchiviste as TypeDocumentGED)) {
    return typeArchiviste as TypeDocumentGED;
  }
  // Fallback : anciens types → nouveaux (compatibilité historique)
  const mapping: Record<string, TypeDocumentGED> = {
    'correspondance_entrante': 'courrier',
    'correspondance_sortante': 'courrier',
    'pv_compte_rendu': 'pv',
    'rapport_diagnostic': 'rapport',
    'document_technique': 'rapport',
    'photo_video': 'photo',
    'contrat_assurance': 'contrat',
    'document_administratif': 'attestation',
  };
  return mapping[typeArchiviste] || 'autre';
};

// Déduire la catégorie depuis le type
const deduireCategorieDepuisType = (type: TypeDocumentGED): CategorieDocument => {
  const mapping: Record<string, CategorieDocument> = {
    'pv': 'juridique',
    'contrat': 'juridique',
    'reglement': 'juridique',
    'edd': 'juridique',
    'facture': 'comptabilite',
    'devis': 'travaux',
    'rapport': 'technique',
    'plan': 'technique',
    'dpe': 'technique',
    'attestation': 'technique',
    'courrier': 'administratif',
    'email': 'administratif',
    'permis': 'administratif',
    'photo': 'technique',
    'note': 'interne_cs',
    'autre': 'administratif'
  };
  return mapping[type] || 'administratif';
};

// Formater la taille du fichier
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 o';
  const k = 1024;
  const sizes = ['o', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Formater la date
const formatDate = (timestamp: any): string => {
  if (!timestamp) return 'Non renseigné';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return 'Non renseigné';
  return date.toLocaleDateString('fr-FR');
};

// Icône selon le type de fichier
const getFileIcon = (fichierType: string) => {
  if (fichierType?.includes('pdf')) {
    return (
      <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      </svg>
    );
  }
  if (fichierType?.includes('image') || fichierType?.includes('jpg') || fichierType?.includes('png')) {
    return (
      <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
      </svg>
    );
  }
  if (fichierType?.includes('word') || fichierType?.includes('document')) {
    return (
      <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      </svg>
    );
  }
  if (fichierType?.includes('sheet') || fichierType?.includes('excel')) {
    return (
      <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
    </svg>
  );
};

// Icône de visibilité
const VisibilityBadge = ({ visibilite }: { visibilite?: 'cs_only' | 'public' }) => {
  if (visibilite === 'public') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" title="Visible par les copropriétaires">
        <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        Public
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" title="Réservé au Conseil Syndical">
      <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
      CS
    </span>
  );
};

export default function DocumentsPage() {
  const { userProfile, firebaseUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { coproprieteId } = useCopropriete();
  
  // Session 75 : Helper pour la permission de suppression
  // Seuls Gestionnaire et Admin SaaS peuvent supprimer des documents
  const canDeleteDocument = userProfile?.isGestionnaireSaas === true || 
                            userProfile?.role === 'gestionnaire' || 
                            userProfile?.role === 'admin_saas';
  
  // État vue active
  const [viewMode, setViewMode] = useState<ViewMode>('categories');
  
  // États liste
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [interlocuteurs, setInterlocuteurs] = useState<Interlocuteur[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // États filtres enrichis
  const [filtreType, setFiltreType] = useState<string>('');
  const [filtreCategorie, setFiltreCategorie] = useState<string>('');
  const [filtreVisibilite, setFiltreVisibilite] = useState<string>('');
  const [filtreRecherche, setFiltreRecherche] = useState('');

  // États SENTINELLE Gardien
  const [showGardienModal, setShowGardienModal] = useState(false);
  const [analyseGardien, setAnalyseGardien] = useState<AnalyseGardienResponse | null>(null);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const [etapeAnalyse, setEtapeAnalyse] = useState<'gardien' | 'archiviste' | null>(null);

  // États SENTINELLE Archiviste
  const [suggestionArchiviste, setSuggestionArchiviste] = useState<DocumentSuggestion | null>(null);
  const [tempFilePath, setTempFilePath] = useState<string | null>(null);
  const [tempFileUrl, setTempFileUrl] = useState<string | null>(null);

  // États modal upload
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitre, setUploadTitre] = useState('');
  const [uploadType, setUploadType] = useState<TypeDocumentGED>('autre');
  const [uploadCategorie, setUploadCategorie] = useState<CategorieDocument>('administratif');
  const [uploadInterlocuteurId, setUploadInterlocuteurId] = useState('');
  const [uploadDateDocument, setUploadDateDocument] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadVisibilite, setUploadVisibilite] = useState<'cs_only' | 'public'>('cs_only');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  // États liaison Affaire (Session 66)
  const [uploadAffaireId, setUploadAffaireId] = useState('');
  const [uploadAffaireNom, setUploadAffaireNom] = useState('');
  const [affairesDisponibles, setAffairesDisponibles] = useState<Affaire[]>([]);

  // États modal suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentData | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // État message succès
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Auto-masquer le message de succès
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Charger les documents, interlocuteurs et affaires
  useEffect(() => {
    const fetchData = async () => {
      if (!coproprieteId) {
        setLoading(false);
        return;
      }

      try {
        // Charger les documents
        const docsQuery = query(
          collection(db, 'documents'),
          where('coproprieteId', '==', coproprieteId),
          orderBy('createdAt', 'desc')
        );
        const docsSnapshot = await getDocs(docsQuery);
        const docsData = docsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as DocumentData[];
        setDocuments(docsData);

        // Charger les interlocuteurs pour le filtre et l'upload
        const intQuery = query(
          collection(db, 'interlocuteurs'),
          where('coproprieteId', '==', coproprieteId),
          orderBy('raisonSociale')
        );
        const intSnapshot = await getDocs(intQuery);
        const intData = intSnapshot.docs.map(doc => ({
          id: doc.id,
          raisonSociale: doc.data().raisonSociale,
          type: doc.data().type
        })) as Interlocuteur[];
        setInterlocuteurs(intData);

        // Charger les affaires ouvertes pour le dropdown (Session 66)
        const affaires = await getAffairesByCopropriete(coproprieteId);
        const affairesOuvertes = affaires.filter(a => a.statut !== 'cloturee');
        setAffairesDisponibles(affairesOuvertes);

      } catch (err: any) {
        console.error('Erreur chargement:', err);
        if (err.message?.includes('index')) {
          setError('Index Firestore requis. Vérifiez la console pour le lien de création.');
        } else {
          setError('Erreur lors du chargement des documents');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [coproprieteId]);

  // Filtrer les documents (appliqué à toutes les vues)
  const documentsFiltres = documents.filter(doc => {
    if (filtreType && doc.type !== filtreType) return false;
    if (filtreCategorie && doc.categorie !== filtreCategorie) return false;
    if (filtreVisibilite && doc.visibilite !== filtreVisibilite) return false;
    if (filtreRecherche) {
      const recherche = filtreRecherche.toLowerCase();
      const matchTitre = doc.titre?.toLowerCase().includes(recherche);
      const matchDescription = doc.description?.toLowerCase().includes(recherche);
      const matchInterlocuteur = doc.interlocuteurNom?.toLowerCase().includes(recherche);
      if (!matchTitre && !matchDescription && !matchInterlocuteur) return false;
    }
    return true;
  });

  // Compter les filtres actifs
  const filtresActifs = [filtreType, filtreCategorie, filtreVisibilite, filtreRecherche].filter(Boolean).length;

  // SESSION 83 : Détecter si une recherche textuelle est active
  const rechercheActive = filtreRecherche.trim().length > 0;

  // Réinitialiser les filtres
  const resetFiltres = () => {
    setFiltreType('');
    setFiltreCategorie('');
    setFiltreVisibilite('');
    setFiltreRecherche('');
  };

  // Gérer la sélection de fichier - FLUX COMPLET Gardien → Archiviste
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !coproprieteId) return;

    // 1. Vérification locale rapide (format + taille)
    const verification = verifierFichier(file);
    if (!verification.accepte) {
      setUploadError(verification.erreur || 'Fichier non accepté');
      return;
    }

    // Stocker le fichier pour usage ultérieur
    setUploadFile(file);
    setUploadError(null);
    setAnalyseEnCours(true);
    setEtapeAnalyse('gardien');

    try {
      // 2. ÉTAPE 1 : GARDIEN - Analyse pertinence et doublons
      const analyseGardienResult = await analyserAvantUpload(file, coproprieteId);
      setAnalyseGardien(analyseGardienResult);

      // Décision basée sur le champ "suggestions.archiver" (pas le verdict)
      // archiver=true → continuer vers l'Archiviste
      // archiver=false → montrer modal Gardien pour confirmation/rejet
      if (analyseGardienResult.suggestions.archiver) {
        // Le Gardien autorise l'archivage → enchaîner avec l'Archiviste
        await lancerAnalyseArchiviste(file);
      } else {
        // Le Gardien refuse ou a des doutes sérieux → demander confirmation
        setShowGardienModal(true);
        setAnalyseEnCours(false);
        setEtapeAnalyse(null);
      }

    } catch (err) {
      console.error('Erreur analyse:', err);
      // En cas d'erreur, ouvrir le formulaire vide
      ouvrirFormulaireVide(file);
    }
  };

  // Lancer l'analyse Archiviste (après validation Gardien)
  const lancerAnalyseArchiviste = async (file: File) => {
    if (!coproprieteId || !userProfile) {
      ouvrirFormulaireVide(file);
      return;
    }

    setAnalyseEnCours(true);
    setEtapeAnalyse('archiviste');

    try {
      // Upload temporaire pour permettre à l'Archiviste d'analyser
      const { url, path } = await uploadTempFile(file, coproprieteId, userProfile.id);
      setTempFilePath(path);
      setTempFileUrl(url);

      // Appeler l'Archiviste
      const resultatArchiviste = await analyserArchiviste(url, file.name, file.type, coproprieteId);

      if (resultatArchiviste.success && resultatArchiviste.suggestion) {
        // Pré-remplir le formulaire avec les suggestions
        preRemplirFormulaire(resultatArchiviste.suggestion, file);
        setSuggestionArchiviste(resultatArchiviste.suggestion);
      } else {
        // L'Archiviste a échoué, ouvrir formulaire avec valeurs par défaut
        ouvrirFormulaireVide(file);
      }

    } catch (err) {
      console.error('Erreur Archiviste:', err);
      ouvrirFormulaireVide(file);
    } finally {
      setAnalyseEnCours(false);
      setEtapeAnalyse(null);
    }
  };

  // Pré-remplir le formulaire avec les suggestions de l'Archiviste
  const preRemplirFormulaire = (suggestion: DocumentSuggestion, file: File) => {
    // Titre
    setUploadTitre(suggestion.titre || file.name.replace(/\.[^/.]+$/, ''));
    
    // Type (mapper depuis les anciens types vers les nouveaux si nécessaire)
    const typeGED = mapTypeArchivisteVersGED(suggestion.typeDocument);
    setUploadType(typeGED);
    
    // Session 106 : Catégorie — utiliser celle retournée par SENTINELLE si disponible
    // Sinon fallback vers la déduction depuis le type (compatibilité ancienne version)
    const suggestionCategorie = (suggestion as any).categorie;
    if (suggestionCategorie && CATEGORIES_DOCUMENT_OPTIONS.some(c => c.value === suggestionCategorie)) {
      setUploadCategorie(suggestionCategorie as CategorieDocument);
    } else {
      const categorie = deduireCategorieDepuisType(typeGED);
      setUploadCategorie(categorie);
    }
    
    // Date du document
    if (suggestion.dateDocument) {
      setUploadDateDocument(suggestion.dateDocument);
    } else {
      setUploadDateDocument('');
    }
    
    // Interlocuteur
    if (suggestion.interlocuteurId) {
      setUploadInterlocuteurId(suggestion.interlocuteurId);
    } else {
      setUploadInterlocuteurId('');
    }
    
    // Description
    setUploadDescription(suggestion.description || '');
    
    // Visibilité par défaut
    setUploadVisibilite('cs_only');
    
    // Affaire liée (Session 66 - NOUVEAU)
    if (suggestion.affaireId) {
      setUploadAffaireId(suggestion.affaireId);
      setUploadAffaireNom(suggestion.affaireNom || '');
    } else {
      setUploadAffaireId('');
      setUploadAffaireNom('');
    }
    
    // Ouvrir le modal
    setShowModal(true);
  };

  // Ouvrir le formulaire avec valeurs par défaut (fallback)
  const ouvrirFormulaireVide = (file: File) => {
    const nomSansExtension = file.name.replace(/\.[^/.]+$/, '');
    setUploadTitre(nomSansExtension);
    setUploadType('autre');
    setUploadCategorie('administratif');
    setUploadInterlocuteurId('');
    setUploadDateDocument('');
    setUploadDescription('');
    setUploadVisibilite('cs_only');
    setUploadAffaireId('');
    setUploadAffaireNom('');
    setShowModal(true);
    setAnalyseEnCours(false);
    setEtapeAnalyse(null);
  };

  // Gérer l'acceptation depuis le modal Gardien → lancer l'Archiviste
  const handleGardienAccepter = async () => {
    setShowGardienModal(false);
    if (uploadFile) {
      await lancerAnalyseArchiviste(uploadFile);
    }
  };

  // Gérer le rejet depuis le modal Gardien
  const handleGardienRejeter = () => {
    setShowGardienModal(false);
    setAnalyseGardien(null);
    resetUploadForm();
  };

  // Voir un document doublon
  const handleVoirDoublon = (documentId: string) => {
    setShowGardienModal(false);
    navigate(`/admin/documents/${documentId}`);
  };

  // Réinitialiser le formulaire d'upload
  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadTitre('');
    setUploadType('autre');
    setUploadCategorie('administratif');
    setUploadInterlocuteurId('');
    setUploadDateDocument('');
    setUploadDescription('');
    setUploadVisibilite('cs_only');
    setUploadAffaireId('');
    setUploadAffaireNom('');
    setUploadError(null);
    setSuggestionArchiviste(null);
    setTempFilePath(null);
    setTempFileUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Fermer le modal upload
  const closeModal = async () => {
    // Nettoyer le fichier temporaire si présent
    if (tempFilePath) {
      try {
        await deleteTempFile(tempFilePath);
      } catch (err) {
        console.warn('Erreur nettoyage fichier temp:', err);
      }
    }
    setShowModal(false);
    resetUploadForm();
  };

  // Ouvrir le modal de suppression
  const openDeleteModal = (document: DocumentData) => {
    setDocumentToDelete(document);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  // Fermer le modal de suppression
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
    setDeleteError(null);
  };

  // Construire le nom complet de l'utilisateur
  const getUserFullName = (): string => {
    if (!userProfile) return 'Utilisateur';
    const parts = [userProfile.prenom, userProfile.nom].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : userProfile.email;
  };

  // Gérer le changement d'affaire dans le dropdown
  const handleAffaireChange = (affaireId: string) => {
    setUploadAffaireId(affaireId);
    if (affaireId) {
      const affaire = affairesDisponibles.find(a => a.id === affaireId);
      setUploadAffaireNom(affaire?.titre || '');
    } else {
      setUploadAffaireNom('');
    }
  };

  // Gérer l'upload
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile || !coproprieteId || !userProfile || !firebaseUser) return;

    setUploading(true);
    setUploadError(null);

    try {
      let fichierUrl: string;

      // Si on a déjà uploadé en temp (via Archiviste), déplacer vers final
      // Sinon, uploader maintenant
      if (tempFileUrl) {
        // Le fichier est déjà uploadé, on utilise l'URL temp
        fichierUrl = tempFileUrl;
      } else {
        // Upload du fichier vers Storage
        const fileName = `${Date.now()}_${uploadFile.name}`;
        const storageRef = ref(storage, `coproprietes/${coproprieteId}/documents/${fileName}`);
        await uploadBytes(storageRef, uploadFile);
        fichierUrl = await getDownloadURL(storageRef);
      }

      // Trouver le nom de l'interlocuteur
      const interlocuteur = interlocuteurs.find(i => i.id === uploadInterlocuteurId);

      // Créer le document dans Firestore
      const docId = `doc_${Date.now()}`;
      const docRef = doc(db, 'documents', docId);
      
      const userFullName = getUserFullName();
      
      await setDoc(docRef, {
        coproprieteId,
        titre: uploadTitre,
        description: uploadDescription || null,
        type: uploadType,
        categorie: uploadCategorie,
        fichierUrl,
        fichierNom: uploadFile.name,
        fichierTaille: uploadFile.size,
        fichierType: uploadFile.type,
        interlocuteurId: uploadInterlocuteurId || null,
        interlocuteurNom: interlocuteur?.raisonSociale || null,
        dateDocument: uploadDateDocument ? Timestamp.fromDate(new Date(uploadDateDocument)) : null,
        visibilite: uploadVisibilite,
        // Liaison Affaire (Session 66)
        affaireId: uploadAffaireId || null,
        affaireNom: uploadAffaireNom || null,
        // Traçabilité
        uploadeParId: userProfile.id,
        uploadeParNom: userFullName,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        // Session 120 : Stocker anomalies et donneesCles au premier niveau (contextBuilder)
        anomalies: suggestionArchiviste ? ((suggestionArchiviste as any).anomalies || []) : [],
        donneesCles: suggestionArchiviste ? ((suggestionArchiviste as any).donneesCles || {}) : {},
        // Métadonnées SENTINELLE
        sentinelle: suggestionArchiviste ? {
          analyseEffectuee: true,
          confidence: suggestionArchiviste.confidence,
          explication: suggestionArchiviste.explication
        } : null,
        // Session 120 : Analyse complète SENTINELLE (même structure que VIGIE)
        analyseSentinelle: suggestionArchiviste ? {
          confiance: suggestionArchiviste.confidence,
          explication: suggestionArchiviste.explication,
          actionsRecommandees: suggestionArchiviste.actionsRecommandees || [],
          anomalies: (suggestionArchiviste as any).anomalies || [],
          donneesCles: (suggestionArchiviste as any).donneesCles || {},
          dateAnalyse: Timestamp.now()
        } : null
      });

      // Session 66 : Si une affaire est liée, rattacher le document à l'affaire
      if (uploadAffaireId) {
        try {
          await rattacherDocumentAffaire(
            uploadAffaireId,
            docId,
            uploadTitre,
            firebaseUser.uid,
            userFullName
          );
        } catch (err) {
          console.warn('Erreur rattachement affaire (non bloquant):', err);
        }
      }

      // Ajouter le document à la liste locale
      const newDoc: DocumentData = {
        id: docId,
        coproprieteId,
        titre: uploadTitre,
        description: uploadDescription || undefined,
        type: uploadType,
        categorie: uploadCategorie,
        fichierUrl,
        fichierNom: uploadFile.name,
        fichierTaille: uploadFile.size,
        fichierType: uploadFile.type,
        interlocuteurId: uploadInterlocuteurId || undefined,
        interlocuteurNom: interlocuteur?.raisonSociale,
        dateDocument: uploadDateDocument ? Timestamp.fromDate(new Date(uploadDateDocument)) : null,
        uploadeParNom: userFullName,
        createdAt: Timestamp.now(),
        visibilite: uploadVisibilite,
        affaireId: uploadAffaireId || undefined,
        affaireTitre: uploadAffaireNom || undefined
      };

      setDocuments(prev => [newDoc, ...prev]);
      setSuccessMessage('Document ajouté avec succès');
      
      // Fermer sans supprimer le fichier temp (il est maintenant le fichier final)
      setShowModal(false);
      setTempFilePath(null);
      setTempFileUrl(null);
      resetUploadForm();

    } catch (err: any) {
      console.error('Erreur upload:', err);
      setUploadError('Erreur lors de l\'upload du document');
    } finally {
      setUploading(false);
    }
  };

  // Gérer la suppression
  const handleDelete = async () => {
    if (!documentToDelete) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await deleteDocument(documentToDelete.id, documentToDelete.fichierUrl);
      setDocuments(prev => prev.filter(d => d.id !== documentToDelete.id));
      setSuccessMessage('Document supprimé avec succès');
      closeDeleteModal();
    } catch (err: any) {
      console.error('Erreur suppression:', err);
      setDeleteError('Erreur lors de la suppression du document');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
            <p className="text-gray-600 mt-1">
              {documentsFiltres.length} document{documentsFiltres.length > 1 ? 's' : ''} 
              {filtresActifs > 0 && ` (${filtresActifs} filtre${filtresActifs > 1 ? 's' : ''} actif${filtresActifs > 1 ? 's' : ''})`}
            </p>
          </div>
          
          {/* Bouton ajouter */}
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={analyseEnCours}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {analyseEnCours ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>
                    {etapeAnalyse === 'gardien' ? 'Vérification...' : 'Analyse...'}
                  </span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Ajouter un document</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {uploadError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {uploadError}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        )}

        {/* Sélecteur de vue + Filtres */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          {/* Tabs de vue */}
          <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-500 mr-2">Vue :</span>
            <button
              onClick={() => setViewMode('categories')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'categories'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Par catégorie
            </button>
            <button
              onClick={() => setViewMode('affaires')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'affaires'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Par affaire
            </button>
            <button
              onClick={() => setViewMode('liste')}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'liste'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              Liste
            </button>
          </div>

          {/* Filtres */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {/* Recherche */}
            <div className="lg:col-span-2">
              <div className="relative">
                <input
                  type="text"
                  value={filtreRecherche}
                  onChange={(e) => setFiltreRecherche(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Type */}
            <select
              value={filtreType}
              onChange={(e) => setFiltreType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les types</option>
              {TYPES_DOCUMENT.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            {/* Catégorie */}
            <select
              value={filtreCategorie}
              onChange={(e) => setFiltreCategorie(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Toutes catégories</option>
              {CATEGORIES_DOCUMENT_OPTIONS.map(({ value, label, code }) => (
                <option key={value} value={value}>{code} - {label}</option>
              ))}
            </select>

            {/* Visibilité */}
            <select
              value={filtreVisibilite}
              onChange={(e) => setFiltreVisibilite(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Toute visibilité</option>
              <option value="cs_only">CS uniquement</option>
              <option value="public">Public</option>
            </select>
          </div>

          {/* Reset filtres */}
          {filtresActifs > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={resetFiltres}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Réinitialiser les filtres
              </button>
              <span className="text-sm text-gray-500">
                ({filtresActifs} filtre{filtresActifs > 1 ? 's' : ''} actif{filtresActifs > 1 ? 's' : ''})
              </span>
            </div>
          )}

          {/* SESSION 83 : Résultats de recherche affichés directement sous la barre */}
          {rechercheActive && documentsFiltres.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="font-medium text-gray-700">
                  {documentsFiltres.length} résultat{documentsFiltres.length > 1 ? 's' : ''} pour "{filtreRecherche}"
                </span>
              </div>
              <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200">
                {documentsFiltres.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 hover:bg-white transition-colors">
                    <Link 
                      to={`/admin/documents/${doc.id}`}
                      className="flex items-center gap-3 flex-1 min-w-0"
                    >
                      {getFileIcon(doc.fichierType)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 hover:text-blue-600 truncate">
                            {doc.titre}
                          </span>
                          <VisibilityBadge visibilite={doc.visibilite} />
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-medium ${getTypeColor(doc.type)}`}>
                            {getTypeDocumentLabel(doc.type)}
                          </span>
                          <span>·</span>
                          <span>{formatDate(doc.dateDocument)}</span>
                          {doc.categorie && (
                            <>
                              <span>·</span>
                              <span>{getCategorieLabel(doc.categorie)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </Link>
                    <div className="flex items-center gap-1 ml-2">
                      <a
                        href={doc.fichierUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Télécharger"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </a>
                      <Link
                        to={`/admin/documents/${doc.id}`}
                        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Voir détails"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                      {canDeleteDocument && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            openDeleteModal(doc);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Contenu selon la vue - SESSION 83 : Masqué si recherche active avec résultats */}
        {!rechercheActive && documentsFiltres.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun document</h3>
            <p className="text-gray-500 mb-4">
              Commencez par ajouter votre premier document.
            </p>
          </div>
        ) : !rechercheActive && viewMode === 'categories' ? (
          <DocumentCategoryAccordion 
            documents={documentsFiltres}
          />
        ) : !rechercheActive && viewMode === 'affaires' ? (
          <DocumentAffaireGroup
            documents={documentsFiltres}
            coproprieteId={coproprieteId || ''}
          />
        ) : !rechercheActive ? (
          /* Vue Liste classique */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Catégorie</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">Interlocuteur</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {documentsFiltres.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {getFileIcon(doc.fichierType)}
                          <div className="min-w-0">
                            <Link 
                              to={`/admin/documents/${doc.id}`}
                              className="font-medium text-gray-900 hover:text-blue-600 truncate block"
                            >
                              {doc.titre}
                            </Link>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-gray-500">{formatFileSize(doc.fichierTaille)}</span>
                              <VisibilityBadge visibilite={doc.visibilite} />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(doc.type)}`}>
                          {getTypeDocumentLabel(doc.type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {doc.categorie && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                            {getCategorieLabel(doc.categorie)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">
                        {formatDate(doc.dateDocument)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 hidden xl:table-cell">
                        {doc.interlocuteurNom || '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={doc.fichierUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Télécharger"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </a>
                          <Link
                            to={`/admin/documents/${doc.id}`}
                            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Voir détails"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </Link>
                          {/* Session 75 : Corbeille visible uniquement pour Gestionnaire/Admin SaaS */}
                          {canDeleteDocument && (
                            <button
                              onClick={() => openDeleteModal(doc)}
                              className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                              title="Supprimer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {/* SESSION 83 : Message si recherche active mais aucun résultat */}
        {rechercheActive && documentsFiltres.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun résultat</h3>
            <p className="text-gray-500 mb-4">
              Aucun document ne correspond à "{filtreRecherche}"
            </p>
            <button
              onClick={resetFiltres}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Effacer la recherche
            </button>
          </div>
        )}

        {/* Modal Gardien */}
        {showGardienModal && analyseGardien && (
          <SentinelleGardienModal
            isOpen={showGardienModal}
            onClose={handleGardienRejeter}
            fileName={uploadFile?.name || ''}
            analyse={analyseGardien}
            onAccepter={handleGardienAccepter}
            onRejeter={handleGardienRejeter}
            onVoirDoublon={handleVoirDoublon}
          />
        )}

        {/* Modal Upload/Validation */}
        {showModal && uploadFile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                {/* En-tête */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Valider le classement
                  </h2>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Bannière SENTINELLE si pré-rempli */}
                {suggestionArchiviste && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">✨</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-blue-800">Pré-rempli par SENTINELLE</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            suggestionArchiviste.confidence === 'haute' 
                              ? 'bg-green-100 text-green-700' 
                              : suggestionArchiviste.confidence === 'moyenne'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            Confiance {suggestionArchiviste.confidence}
                          </span>
                        </div>
                        <p className="text-sm text-blue-700">{suggestionArchiviste.explication}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Info fichier */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                  {getFileIcon(uploadFile.type)}
                  <div>
                    <p className="font-medium text-gray-900">{uploadFile.name}</p>
                    <p className="text-sm text-gray-500">{formatFileSize(uploadFile.size)}</p>
                  </div>
                </div>

                {/* Formulaire */}
                <form onSubmit={handleUpload} className="space-y-4">
                  {/* Titre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titre *
                    </label>
                    <input
                      type="text"
                      value={uploadTitre}
                      onChange={(e) => setUploadTitre(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de document *
                    </label>
                    <select
                      value={uploadType}
                      onChange={(e) => setUploadType(e.target.value as TypeDocumentGED)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {TYPES_DOCUMENT.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Catégorie */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Catégorie *
                    </label>
                    <select
                      value={uploadCategorie}
                      onChange={(e) => setUploadCategorie(e.target.value as CategorieDocument)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {CATEGORIES_DOCUMENT_OPTIONS.map(({ value, label, code }) => (
                        <option key={value} value={value}>{code} - {label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Visibilité */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Visibilité *
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="visibilite"
                          value="cs_only"
                          checked={uploadVisibilite === 'cs_only'}
                          onChange={() => setUploadVisibilite('cs_only')}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">CS uniquement</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="visibilite"
                          value="public"
                          checked={uploadVisibilite === 'public'}
                          onChange={() => setUploadVisibilite('public')}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Public (copropriétaires)</span>
                      </label>
                    </div>
                  </div>

                  {/* Interlocuteur */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Interlocuteur concerné
                    </label>
                    <select
                      value={uploadInterlocuteurId}
                      onChange={(e) => setUploadInterlocuteurId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Aucun / Non applicable</option>
                      {interlocuteurs.map((int) => (
                        <option key={int.id} value={int.id}>{int.raisonSociale}</option>
                      ))}
                    </select>
                  </div>

                  {/* Affaire liée (Session 66 - NOUVEAU) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Affaire liée
                      {suggestionArchiviste?.affaireId && (
                        <span className="ml-2 text-xs text-blue-600">(suggéré par SENTINELLE)</span>
                      )}
                    </label>
                    <select
                      value={uploadAffaireId}
                      onChange={(e) => handleAffaireChange(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        suggestionArchiviste?.affaireId && uploadAffaireId === suggestionArchiviste.affaireId
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="">Aucune affaire liée</option>
                      {affairesDisponibles.map((affaire) => (
                        <option key={affaire.id} value={affaire.id}>
                          {affaire.numero} - {affaire.titre}
                        </option>
                      ))}
                    </select>
                    {affairesDisponibles.length === 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        Aucune affaire ouverte. <Link to="/admin/affaires/nouvelle" className="text-blue-600 hover:underline">Créer une affaire</Link>
                      </p>
                    )}
                  </div>

                  {/* Date du document */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date du document
                    </label>
                    <input
                      type="date"
                      value={uploadDateDocument}
                      onChange={(e) => setUploadDateDocument(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={uploadDescription}
                      onChange={(e) => setUploadDescription(e.target.value)}
                      placeholder="Informations complémentaires..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Boutons */}
                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={uploading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {uploading ? 'Enregistrement...' : suggestionArchiviste ? 'Valider et archiver' : 'Enregistrer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal Suppression */}
        {showDeleteModal && documentToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                {/* Icône alerte */}
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>

                {/* Titre */}
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Confirmer la suppression
                </h3>

                {/* Message */}
                <p className="text-gray-600 text-center mb-2">
                  Voulez-vous vraiment supprimer ce document ?
                </p>
                <p className="text-sm font-medium text-gray-900 text-center mb-4 px-4 py-2 bg-gray-100 rounded">
                  {documentToDelete.titre}
                </p>
                <p className="text-sm text-red-600 text-center mb-6">
                  Cette action est irréversible. Le fichier sera définitivement supprimé.
                </p>

                {/* Erreur */}
                {deleteError && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {deleteError}
                  </div>
                )}

                {/* Boutons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeDeleteModal}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {deleting ? 'Suppression...' : 'Confirmer la suppression'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
